import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Button } from "shared/ui/button"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Checkbox } from "shared/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { RoleDto, getRoleMenus, searchRoles, updateRoleMenus } from "modules/admin/api/roleService"
import { MenuDto, PagedResultDto, searchMenus } from "modules/admin/api/menuService"
import { Badge } from "shared/ui/badge"
import { RefreshCw, Save } from "lucide-react"
import { DEFAULT_TENANT_ID } from "shared/utils/tenantContext"

interface RoleMenuMappingProps {
  currentUser?: { name?: string }
}

export function RoleMenuMapping({ currentUser }: RoleMenuMappingProps) {
  const [roles, setRoles] = useState<RoleDto[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string>("")
  const [menus, setMenus] = useState<MenuDto[]>([])
  const [assignedMenuIds, setAssignedMenuIds] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [menuSearch, setMenuSearch] = useState("")
  const [menuPage, setMenuPage] = useState(1)
  const [menuPageSize] = useState(10)

  const loadRoles = useCallback(async () => {
    try {
      const result: PagedResultDto<RoleDto> = await searchRoles({ page: 1, pageSize: 200 })
      setRoles(result.items ?? [])
      if (!selectedRoleId && result.items?.length) {
        setSelectedRoleId(result.items[0].roleId)
      }
    } catch (err) {
      console.error(err)
      toast.error("โหลดรายการ Role ไม่สำเร็จ")
    }
  }, [selectedRoleId])

  const loadMenus = useCallback(async () => {
    try {
      const result: PagedResultDto<MenuDto> = await searchMenus({ tenantId: DEFAULT_TENANT_ID, page: 1, pageSize: 500 })
      setMenus(result.items ?? [])
    } catch (err) {
      console.error(err)
      toast.error("โหลดรายการเมนูไม่สำเร็จ")
    }
  }, [])

  const loadRoleMenus = useCallback(async (roleId: string) => {
    if (!roleId) return
    setIsLoading(true)
    try {
      const menuIds = await getRoleMenus(roleId)
      setAssignedMenuIds(new Set(menuIds))
    } catch (err) {
      console.error(err)
      toast.error("โหลดเมนูของ Role ไม่สำเร็จ")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadRoles()
    loadMenus()
  }, [loadRoles, loadMenus])

  useEffect(() => {
    if (selectedRoleId) {
      loadRoleMenus(selectedRoleId)
    }
  }, [selectedRoleId, loadRoleMenus])

  const handleToggle = (menuId: string) => {
    setAssignedMenuIds((prev) => {
      const next = new Set(prev)
      if (next.has(menuId)) {
        next.delete(menuId)
      } else {
        next.add(menuId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setAssignedMenuIds(new Set(menus.map((m) => m.menuId)))
  }

  const handleClearAll = () => {
    setAssignedMenuIds(new Set())
  }

  const handleSave = async () => {
    if (!selectedRoleId) return
    setIsSaving(true)
    try {
      await updateRoleMenus(selectedRoleId, Array.from(assignedMenuIds))
      toast.success("บันทึกเมนูของ Role สำเร็จ")
    } catch (err: any) {
      console.error(err)
      const msg = err?.response?.data ?? "บันทึกไม่สำเร็จ"
      toast.error(msg)
    } finally {
      setIsSaving(false)
    }
  }

  const filteredMenus = useMemo(() => {
    if (!menuSearch) return menus
    const term = menuSearch.toLowerCase()
    return menus.filter(
      (m) =>
        m.code.toLowerCase().includes(term) ||
        m.label.toLowerCase().includes(term) ||
        (m.routePath ?? "").toLowerCase().includes(term)
    )
  }, [menus, menuSearch])

  const totalMenuPages = useMemo(
    () => Math.max(1, Math.ceil(filteredMenus.length / menuPageSize)),
    [filteredMenus.length, menuPageSize]
  )

  const pagedMenus = useMemo(() => {
    const start = (menuPage - 1) * menuPageSize
    return filteredMenus.slice(start, start + menuPageSize)
  }, [filteredMenus, menuPage, menuPageSize])

  const currentRole = roles.find((r) => r.roleId === selectedRoleId)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Role-Menu Mapping</h1>
          <p className="text-gray-600 text-sm">
            ผูกเมนูที่อนุญาตให้แต่ละ Role ใช้งาน {currentUser?.name ? `(login: ${currentUser.name})` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => selectedRoleId && loadRoleMenus(selectedRoleId)} disabled={isLoading}>
            <RefreshCw className="h-4 w-4 mr-1" /> รีเฟรช
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !selectedRoleId}>
            <Save className="h-4 w-4 mr-1" /> บันทึก
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>เลือก Role</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={selectedRoleId}
            onValueChange={(val) => setSelectedRoleId(val)}
          >
            <SelectTrigger className="w-full md:w-80">
              <SelectValue placeholder="เลือก Role" />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.roleId} value={role.roleId}>
                  <div className="flex items-center gap-2">
                    <span>{role.displayName}</span>
                    {role.isSystem && <Badge variant="outline">System</Badge>}
                    {!role.isActive && <Badge variant="outline" className="border-red-200 text-red-600">Inactive</Badge>}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {currentRole && (
            <div className="text-sm text-gray-600">
              Code: <span className="font-medium">{currentRole.code}</span>{" "}
              {currentRole.isSystem && <Badge variant="secondary">System</Badge>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>กำหนดเมนู</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-80">
              <Label className="text-sm text-gray-600">ค้นหาเมนู</Label>
              <Input
                placeholder="ค้นหา code / label / route"
                value={menuSearch}
                onChange={(e) => setMenuSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={handleSelectAll}>
                เลือกทั้งหมด
              </Button>
              <Button type="button" variant="outline" onClick={handleClearAll}>
                ล้างทั้งหมด
              </Button>
            </div>
          </div>

          <div className="border rounded-md p-3 space-y-2">
            {pagedMenus.map((menu) => (
              <label key={menu.menuId} className="flex items-center gap-3 text-sm text-gray-800">
                <Checkbox
                  checked={assignedMenuIds.has(menu.menuId)}
                  onCheckedChange={() => handleToggle(menu.menuId)}
                />
                <div className="flex-1">
                  <div className="font-medium">{menu.label}</div>
                  <div className="text-xs text-gray-500">
                    {menu.code} {menu.routePath ? `· ${menu.routePath}` : menu.externalUrl ? `· ${menu.externalUrl}` : ""}
                  </div>
                </div>
              </label>
            ))}
            {!filteredMenus.length && <div className="text-sm text-gray-500 text-center py-4">ไม่พบเมนู</div>}
          </div>

          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              แสดง {pagedMenus.length} จาก {filteredMenus.length} รายการ
            </span>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                disabled={menuPage <= 1}
                onClick={() => setMenuPage((p) => Math.max(1, p - 1))}
              >
                ก่อนหน้า
              </Button>
              <span>หน้า {menuPage}/{totalMenuPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={menuPage >= totalMenuPages}
                onClick={() => setMenuPage((p) => Math.min(totalMenuPages, p + 1))}
              >
                ถัดไป
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default RoleMenuMapping
